import React, {useEffect, useMemo, useState} from "react";
import styles from "./page.module.css";
import AreaChart from "@/components/Charts/AreaChart";
import RadialPercentageChart from "@/components/Charts/RadialPercentageChart";
import MetricLineChart from "@/components/Charts/MetricLineChart";
import MetricStackedBarChart from "@/components/Charts/MetricStackedBarChart";
import {useAnalytics} from "@/contexts/AnalyticsContext";
import {Link, useParams} from "wouter";
import DateRangeButtons from "@/components/DateRangeSelector/DateRangeButtons";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { formatValue } from "@/utils/formatUtils";
import PageContainer from "@/app/settings/container/PageContainer";
import Skeleton from "@/components/Skeleton/Skeleton";
import { DateRangeValue, startOfToday, endOfToday } from "@/utils/dateUtils";
import { getLocalStorageItem } from "@/utils/localStorageUtils";
import { useCache } from "@/hooks/cacheHooks/useCache";
import { computeCacheKey } from "../page";
import { ProfitabilityByProductBreakdown } from "@boxem/core-api-schema/lib/seller-analytics";
import { getSkuProfitabilityBySku } from "@/api/seller-analytics";
import { BuyCostTile } from "@/components/BuyCostBox/BuyCostTile";
import { useInventoryContext } from "@/contexts/InventoryContext";
import VariableBuyCostModal from "@/app/inventory/VariableBuyCostModal/VariableBuyCostModal";

const ProfitBySkuIdPage = () => {
    const metrics = [
        { key: 'revenue', label: 'Revenue', color: '#F97316' },
        { key: 'fees', label: 'Fees', color: '#EAB308' },
        { key: 'profit', label: 'Profit', color: '#6366F1' }
    ];

    const params = useParams<{ id: string}>();
    const [variableBuyCostModalOpen, setVariableBuyCostModalOpen] = useState<boolean>(false);

    const [timeFrame, setTimeFrame] = useState<DateRangeValue>(
        getLocalStorageItem('analyticsTimeFrame', {
          from: startOfToday.startOf("month").startOf('day').toDate(),
          to: endOfToday.toDate(),
          label: 'This month'
        })
    );

    const { data: individualSkuProfitBreakDownData, isLoading: isIndividualProfitabilityLoading, mutate: refetchIndividualSkuProfitBreakDownData } = useCache<ProfitabilityByProductBreakdown>(computeCacheKey(timeFrame, `/seller-analytics/product-sales/${params.id}`), () => getSkuProfitabilityBySku({ sku: params.id, interval: timeFrame }));
    const { inventoryFlat, isFetchingInventory } = useInventoryContext();

    const { metricsData, salesRefundData, marginData, averageBuyCostData } = useMemo(() => {
        if (!individualSkuProfitBreakDownData?.salesBreakdownByDate) {
            return { metricsData: null, salesRefundData: null, marginData: null, shippingCostData: null, roiData: null };
        }

        const { salesBreakdownByDate, totalRevenue, totalFees, totalProfit, totalSales, totalRefunds, marginPercent, roiPercent, averageBuyCost, sku } = individualSkuProfitBreakDownData;

        const [metricsChartData, salesRefundChartData, marginChartData, cogsChartData] = salesBreakdownByDate.reduce((acc, item, index) => {
            acc[0].push({
                day: item.date,
                revenue: item.revenue,
                fees: item.fees,
                profit: item.profit
            });

            acc[1].push({
                day: item.date,
                sales: item.sales,
                refunds: item.refunds
            });

            acc[2].push({
                date: item.date,
                value: item.margin
            });

            acc[3].push({
                data: item.date,
                value: item.cogs
            })

            return acc;
        }, [[], [], [], []]);

        return {
            metricsData: {
                chartData: metricsChartData,
                totals: {
                    revenue: { value: totalRevenue },
                    fees: { value: totalFees },
                    profit: { value: totalProfit }
                }
            },
            salesRefundData: {
                chartData: salesRefundChartData,
                totals: [
                    {
                        key: 'sales',
                        label: 'Sales',
                        color: '#f97316',
                        total: totalSales,
                    },
                    {
                        key: 'refunds',
                        label: 'Refunds',
                        color: '#334155',
                        total: totalRefunds,
                    }
                ]
            },
            marginData: {
                chartData: marginChartData,
                total: marginPercent
            },
            cogsData: {
                chartData: cogsChartData,
                total: 0
            },
            roiData: {
                chartData: [],
                total: roiPercent
            },
            averageBuyCostData: {
                total: averageBuyCost,
                sku
            }
        };
    }, [individualSkuProfitBreakDownData]);

    const inventoryItem = useMemo(
        () => {
            if (inventoryFlat && individualSkuProfitBreakDownData?.sku) {
                return inventoryFlat?.find((item) => item.sellerData?.[0]?.sku === individualSkuProfitBreakDownData.sku);
            }
        }, 
        [inventoryFlat, individualSkuProfitBreakDownData], 
    );

    console.log("cool", individualSkuProfitBreakDownData);
    return (
        <PageContainer>
            <div className={styles.pageContainer}>
                <div className={styles.topRowContainer}>
                    <Link className={styles.backButton} href={"/profit-by-sku?analytics=true"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M13.3 17.2998L8.70005 12.6998C8.60005 12.5998 8.52938 12.4915 8.48805 12.3748C8.44605 12.2581 8.42505 12.1331 8.42505 11.9998C8.42505 11.8665 8.44605 11.7415 8.48805 11.6248C8.52938 11.5081 8.60005 11.3998 8.70005 11.2998L13.3 6.6998C13.4834 6.51647 13.7167 6.4248 14 6.4248C14.2834 6.4248 14.5167 6.51647 14.7 6.6998C14.8834 6.88314 14.975 7.11647 14.975 7.3998C14.975 7.68314 14.8834 7.91647 14.7 8.0998L10.8 11.9998L14.7 15.8998C14.8834 16.0831 14.975 16.3165 14.975 16.5998C14.975 16.8831 14.8834 17.1165 14.7 17.2998C14.5167 17.4831 14.2834 17.5748 14 17.5748C13.7167 17.5748 13.4834 17.4831 13.3 17.2998Z"
                                fill="var(--static-text-standard)"/>
                        </svg>
                        <span className={styles.title}>
                            {isIndividualProfitabilityLoading ? <Skeleton width={"100%"} height={32} /> : individualSkuProfitBreakDownData?.productName}
                        </span>
                    </Link>
            <DateRangeButtons
                timeFrame={timeFrame}
                setTimeFrame={setTimeFrame}
            />
            </div>
            <div className={styles.container}>
            <div className={styles.salesLineChart}>
                <MetricLineChart
                    data={metricsData?.chartData}
                    metrics={metrics}
                    totals={metricsData?.totals}
                    valueFormatter={(value) => value >= 1000 ? `$${Math.round(value / 1000)}K` : `$${Math.round(value)}`}
                    xAxisFormatter={(day) => day.toString()}
                    isLoading={isIndividualProfitabilityLoading || !metricsData }
                />
            </div>
            <div className={styles.salesBarChart}>
                <MetricStackedBarChart
                    data={salesRefundData?.chartData}
                    metrics={salesRefundData?.totals}
                    height={'300px'}
                    isLoading={isIndividualProfitabilityLoading || !salesRefundData}
                />
            </div>
            <div className={styles.smallerChartsGrid}>
                <div className={styles.smallChart}>
                    <RadialPercentageChart 
                        percentage={individualSkuProfitBreakDownData?.roiPercent} 
                        title={"ROI %"} 
                        color={"var(--primary-enabled)"}
                        isLoading={isIndividualProfitabilityLoading || !individualSkuProfitBreakDownData}
                    />
                </div>
                <div className={styles.smallChart}>
                    <AreaChart data={marginData?.chartData} title={"Margin"} currentValue={marginData?.total} valueFormatter={(value) => {
                        return `${value}%`
                    }} isLoading={isIndividualProfitabilityLoading || !marginData}/>
                </div>
                <div className={styles.smallChart}>
                    {/* <AreaChart
                        data={shippingCostData?.chartData}
                        title={"Buy Costs"}
                        currentValue={shippingCostData?.total}
                        color={"var(--primary-enabled)"}
                        patternOpacity={0.6}
                        valueFormatter={(value) => {
                            return formatValue(value, 'currency')
                        }}
                        isLoading={isIndividualProfitabilityLoading || !shippingCostData}
                    /> */}
                    <BuyCostTile
                        buyCost={averageBuyCostData?.total} 
                        allowEdit
                        onEdit={() => {
                            setVariableBuyCostModalOpen(true);
                        }}
                        isLoading={isIndividualProfitabilityLoading || isFetchingInventory}
                    />
                </div>
                <div className={styles.smallChart}>
                    <RadialPercentageChart 
                        percentage={individualSkuProfitBreakDownData?.revenuePercent} 
                        title={"% of total Revenue,"} 
                        color={"#5823FF"}
                        isLoading={isIndividualProfitabilityLoading || !individualSkuProfitBreakDownData}
                    />
                </div>
            </div>
        </div>
    </div>
        {variableBuyCostModalOpen &&
            <VariableBuyCostModal
                inventoryItem={inventoryItem}
                open={variableBuyCostModalOpen}
                onOpenChange={(isOpen) => { setVariableBuyCostModalOpen(isOpen) }}
                onClose={() => { setVariableBuyCostModalOpen(false) }}
                onUpdate={() => {
                    refetchIndividualSkuProfitBreakDownData();
                }}
            />
        }
    </PageContainer>
    )
}

export default ProfitBySkuIdPage